/**
 * Lazy, in-memory JSON collection loader.
 *
 * Entity data is generated by `scripts/seed.mjs` into `public/mock-db/*.json`
 * and fetched on first use per collection here — it must never be imported
 * into the JS bundle. Each collection is cached by reference after the first
 * fetch, so the same array instance is returned on every subsequent read
 * within a session (mutating it, e.g. via `mutateCollection`, is visible to
 * every later caller).
 */

const cache = new Map<string, unknown[]>();
const inflight = new Map<string, Promise<unknown[]>>();

async function fetchCollection<T>(name: string): Promise<T[]> {
  const response = await fetch(`/mock-db/${name}.json`);
  if (!response.ok) {
    throw new Error(`Mock collection unavailable: ${name} (${response.status})`);
  }
  return (await response.json()) as T[];
}

export async function getCollection<T>(name: string): Promise<T[]> {
  const cached = cache.get(name);
  if (cached) return cached as T[];

  const pending = inflight.get(name);
  if (pending) return pending as Promise<T[]>;

  const promise = fetchCollection<T>(name).then((data) => {
    cache.set(name, data);
    inflight.delete(name);
    return data;
  });
  inflight.set(name, promise);
  return promise;
}

/** Replaces a cached collection wholesale (used by mutation handlers). */
export function setCollection<T>(name: string, items: T[]): void {
  cache.set(name, items);
}

/** Test-only: clears every cached collection so the next read re-fetches. */
export function resetDb(): void {
  cache.clear();
  inflight.clear();
}
